import type { FC } from 'react';

import Pagination from '@/common-components/Pagination';
import SignupLeadsTable from '@/features/users/components/SignupLeadsTable';
import UserDetailDrawer from '@/features/users/components/UserDetailDrawer';
import UsersTable, { UsersCardList } from '@/features/users/components/UsersTable';
import UsersToolbar from '@/features/users/components/UsersToolbar';
import { useUsersListPage } from '@/features/users/hooks/useUsersListPage.hook';

const UsersListPage: FC = () => {
  const vm = useUsersListPage();

  const rowCount = vm.isLeadsTab ? vm.leads.leads.length : vm.users.length;
  // PAGE_SIZE is 10 in the hook; the previous literal 20 here made the "showing X–Y" line wrong.
  const startIndex = rowCount === 0 ? 0 : (vm.page - 1) * 10 + 1;
  const endIndex = rowCount === 0 ? 0 : startIndex + rowCount - 1;

  return (
    <>
      <div className="flex flex-col gap-6">
        <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-[22px] font-extrabold tracking-tight text-[var(--color-ink)]">
              User Management
            </h1>
            <p className="mt-1 text-[13px] text-[var(--color-ink-muted)]">
              {vm.isLeadsTab
                ? `${vm.total.toLocaleString()} verified their email but never finished signing up`
                : vm.isDeletedTab
                  ? `${vm.total.toLocaleString()} deleted account${vm.total === 1 ? '' : 's'} — retained records, not members`
                  : `${vm.total.toLocaleString()} total user${vm.total === 1 ? '' : 's'}`}
            </p>
          </div>
          <button
            type="button"
            className="inline-flex items-center gap-2 self-start rounded-lg border border-[var(--color-divider)] bg-[var(--color-surface-muted)] px-4 py-2 text-[13px] font-semibold text-[var(--color-ink-muted)] transition-colors hover:bg-[var(--color-surface-muted-2)]"
          >
            📥 Export CSV
          </button>
        </header>

        <section className="overflow-hidden rounded-2xl border border-[var(--color-divider)] bg-white shadow-[var(--shadow-card)]">
          <UsersToolbar
            filter={vm.filter}
            campusId={vm.campusId}
            sort={vm.sort}
            search={vm.search}
            campuses={vm.campuses}
            onChangeFilter={vm.onChangeFilter}
            onChangeCampus={vm.onChangeCampus}
            onChangeSort={vm.onChangeSort}
            onChangeSearch={vm.onChangeSearch}
          />

          {vm.isLoading ? (
            <div className="flex min-h-64 items-center justify-center text-[13px] text-[var(--color-ink-muted)]">
              Loading users…
            </div>
          ) : vm.isError ? (
            <div className="flex min-h-64 flex-col items-center justify-center gap-2 p-6 text-center">
              <p className="text-sm font-semibold text-[var(--color-danger-ink)]">
                Couldn't load users
              </p>
              <p className="text-xs text-[var(--color-ink-muted)]">
                {vm.errorMessage ?? 'Please try again.'}
              </p>
            </div>
          ) : vm.isLeadsTab && vm.leads.leads.length === 0 ? (
            <div className="flex min-h-64 flex-col items-center justify-center gap-2 p-6 text-center">
              <span className="text-3xl">🎉</span>
              <p className="text-sm font-semibold text-[var(--color-ink)]">
                Nobody is stuck in onboarding
              </p>
              <p className="max-w-md text-xs text-[var(--color-ink-muted)]">
                Everyone who verified their college email went on to finish setting up.
              </p>
            </div>
          ) : vm.isLeadsTab ? (
            <SignupLeadsTable leads={vm.leads} />
          ) : vm.users.length === 0 ? (
            <div className="flex min-h-64 flex-col items-center justify-center gap-2 p-6 text-center">
              <span className="text-3xl">{vm.isDeletedTab ? '🗑' : '🔍'}</span>
              <p className="text-sm font-semibold text-[var(--color-ink)]">
                {vm.isDeletedTab ? 'Nobody has deleted their account' : 'No users match'}
              </p>
              <p className="max-w-md text-xs text-[var(--color-ink-muted)]">
                {vm.isDeletedTab
                  ? 'Deleted accounts appear here with the identity they had, kept only for as long as the rental and payment records that reference them.'
                  : 'Try clearing your search or switching filters.'}
              </p>
            </div>
          ) : (
            <>
              <UsersTable
                users={vm.users}
                campusNameById={vm.campusNameById}
                suspendingId={vm.suspendingId}
                onView={vm.onView}
                onSuspend={vm.onSuspend}
                onActivate={vm.onActivate}
              />
              <UsersCardList
                users={vm.users}
                campuses={vm.campuses}
                campusNameById={vm.campusNameById}
                suspendingId={vm.suspendingId}
                onView={vm.onView}
                onSuspend={vm.onSuspend}
                onActivate={vm.onActivate}
              />
            </>
          )}

          <footer className="flex flex-col gap-3 border-t border-[var(--color-divider)] px-4 py-3.5 md:flex-row md:items-center md:justify-between md:px-5">
            <div className="text-[12px] text-[var(--color-ink-muted)]">
              {vm.total === 0
                ? vm.isLeadsTab
                  ? 'Nobody to show'
                  : 'No users to show'
                : `Showing ${startIndex.toLocaleString()}–${endIndex.toLocaleString()} of ${vm.total.toLocaleString()} ${vm.isLeadsTab ? 'people' : vm.isDeletedTab ? 'deleted accounts' : 'users'}`}
            </div>
            {vm.totalPages > 1 ? (
              <Pagination
                page={vm.page}
                totalPages={vm.totalPages}
                onChange={vm.onChangePage}
              />
            ) : null}
          </footer>
        </section>
      </div>

      <UserDetailDrawer
        selectedUser={vm.selectedUser}
        detail={vm.detailData}
        isLoadingDetail={vm.isLoadingDetail}
        isErrorDetail={vm.isErrorDetail}
        campusNameById={vm.campusNameById}
        onClose={vm.onCloseDetail}
        onSuspend={vm.onSuspend}
        onActivate={vm.onActivate}
        suspendingId={vm.suspendingId}
      />
    </>
  );
};

export default UsersListPage;
